import React from "react";
import type { DataTablePagination } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { TransactionItem } from "@models/adminMonitoring";
import { AdminTable, col } from "@components/AdminTable";
import { describeReference, formatCount, moduleLabel, shortRef } from "@utils/monitoringFormat";

interface TransactionsTableProps {
  items: TransactionItem[];
  loading: boolean;
  showOrganisation: boolean;
  onOpen: (item: TransactionItem) => void;
  pagination?: DataTablePagination;
}

/** Reference groups of metering events, one row per group. */
const TransactionsTable: React.FC<TransactionsTableProps> = ({ items, loading, showOrganisation, onOpen, pagination }) => {
  const { t } = useTranslation();

  const transaction = (row: TransactionItem) => {
    if (row.kind === "event") return { primary: t("admin.insights.uncorrelated") };
    const refs = [shortRef(row.external_ref_type), shortRef(row.external_ref_id)].filter(Boolean).join(" / ");
    const entity = row.entity?.label
      ?? (row.entity ? t(`admin.insights.entities.${row.entity.kind}`, { defaultValue: row.entity.kind }) : null);
    // Lead with what the reference names when Core knows it, else the reference itself.
    return entity ? { primary: entity, secondary: refs } : { primary: refs || describeReference(row) };
  };

  const columns = [
    col.date<TransactionItem>("when", t("admin.insights.columns.lastUsed"), (row) => row.last_used),
    col.twoLine<TransactionItem>("transaction", t("admin.insights.columns.transaction"), transaction, { mono: true }),
    col.twoLine<TransactionItem>("service", t("admin.insights.columns.moduleService"), (row) => ({
      primary: moduleLabel(row.module_name), secondary: row.service_name ?? undefined,
    }), { width: "16%" }),
    col.status<TransactionItem>("status", t("admin.insights.columns.entity"), (row) => row.entity?.status),
    col.twoLine<TransactionItem>("owner", t(showOrganisation ? "admin.insights.columns.owner" : "admin.insights.columns.user"),
      (row) => ({
        primary: row.users_count > 1 ? t("admin.insights.usersCount", { count: row.users_count }) : row.user_name ?? row.user_id,
        secondary: showOrganisation ? row.organisation_name ?? row.organisation_id ?? undefined : undefined,
      }), { width: "16%" }),
    col.money<TransactionItem>("cost", t("admin.insights.columns.cost"), (row) => ({
      amount: row.total_cost,
      currency: row.currency,
      warn: row.unpriced_count > 0,
      note: row.unpriced_count
        ? t("admin.insights.unpricedBadge", { count: row.unpriced_count })
        : t("admin.insights.eventsCount", { count: row.event_count, formatted: formatCount(row.event_count) }),
    })),
  ];

  return (
    <AdminTable
      columns={columns}
      data={items}
      getRowKey={(row) => row.group_id}
      onRowClick={onOpen}
      loading={loading}
      emptyMessage={t("admin.insights.empty.transactions")}
      pagination={pagination}
    />
  );
};

export default TransactionsTable;
